import { alpha } from '@mui/material/styles';
import { COMMON_STYLES, ANIMATIONS, SHADOWS, TRANSITIONS, EASINGS, PAGE_LAYOUT } from './constants';

// Utility function to create page container with proper scrolling
export const createPageContainer = (theme, options = {}) => ({
  ...PAGE_LAYOUT.pageContainer,
  backgroundColor: options.backgroundColor || theme.palette.background.default,
  ...options.customStyles
});

// Utility function to create scrollable content area
export const createScrollableContent = (theme, options = {}) => ({
  ...PAGE_LAYOUT.scrollableContent,
  padding: options.padding || '24px',
  ...createScrollbarStyles(theme, theme.palette.primary.main),
  ...options.customStyles
});

// Utility function to create full height layout
export const createFullHeightLayout = (theme, options = {}) => ({
  ...PAGE_LAYOUT.fullHeightLayout,
  backgroundColor: options.backgroundColor || theme.palette.background.default,
  ...options.customStyles
});

// Utility function to create main content area
export const createMainContent = (theme, options = {}) => ({
  ...PAGE_LAYOUT.mainContent,
  ...options.customStyles
});

// Utility function to create page content with responsive padding
export const createPageContent = (theme, options = {}) => {
  const baseStyles = {
    flex: 1,
    minHeight: 0,
    position: 'relative',
    padding: options.responsive ? {
      xs: '16px',
      sm: '20px',
      md: '24px',
      lg: '32px'
    } : (options.padding || '24px'),
  };

  // If scrollable is explicitly set to true, use internal scrolling
  if (options.scrollable) {
    return {
      ...baseStyles,
      overflowY: 'auto',
      overflowX: 'hidden',
      ...createScrollbarStyles(theme, theme.palette.primary.main, 'thin'),
      ...options.customStyles
    };
  }

  // Default: allow document-level scrolling
  return {
    ...baseStyles,
    minHeight: 'auto',
    ...options.customStyles
  };
};

// Utility function to create sidebar layout
export const createSidebarLayout = (theme, options = {}) => ({
  ...PAGE_LAYOUT.sidebarLayout,
  ...options.customStyles
});

// Utility function to create sidebar
export const createSidebar = (theme, options = {}) => ({
  ...PAGE_LAYOUT.sidebar,
  width: options.width || '280px',
  backgroundColor: options.backgroundColor || theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.divider}`,
  ...createScrollbarStyles(theme, theme.palette.primary.main, 'thin'),
  ...options.customStyles
});

// Utility function to create main area (content next to sidebar)
export const createMainArea = (theme, options = {}) => ({
  ...PAGE_LAYOUT.mainArea,
  ...options.customStyles
});

// Utility function to create fixed header
export const createFixedHeader = (theme, options = {}) => ({
  ...PAGE_LAYOUT.fixedHeader,
  backgroundColor: options.backgroundColor || theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.divider}`,
  ...options.customStyles
});

// Utility function to create fixed footer
export const createFixedFooter = (theme, options = {}) => ({
  ...PAGE_LAYOUT.fixedFooter,
  backgroundColor: options.backgroundColor || theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  ...options.customStyles
});

// Utility function to create responsive page wrapper
export const createResponsivePageWrapper = (theme, options = {}) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
  ...options.customStyles
});

// Utility function to create content wrapper with proper scrolling
export const createContentWrapper = (theme, options = {}) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  overflow: 'hidden',
  ...options.customStyles
});

// Utility function to create header-constrained content wrapper
export const createHeaderConstrainedWrapper = (theme, options = {}) => ({
  ...PAGE_LAYOUT.constrainedPageContent,
  ...options.customStyles
});

// Utility function to create content that matches header width
export const createHeaderMatchingContent = (theme, options = {}) => ({
  maxWidth: '1536px', // xl breakpoint - matches header Container maxWidth="xl"
  width: '100%',
  margin: '0 auto',
  px: { xs: 2, sm: 3 },
  py: options.noPadding ? 0 : { xs: 2, sm: 3 },
  position: 'relative',
  ...options.customStyles
});

// Utility function to create scrollable area
export const createScrollableArea = (theme, options = {}) => ({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  minHeight: 0,
  scrollBehavior: 'smooth',
  WebkitOverflowScrolling: 'touch',
  ...createScrollbarStyles(theme, theme.palette.primary.main, options.scrollbarType),
  ...options.customStyles
});

// Utility function to create document-level scrollable page
export const createDocumentScrollablePage = (theme, options = {}) => ({
  minHeight: '100vh',
  width: '100%',
  position: 'relative',
  padding: options.responsive ? {
    xs: '16px',
    sm: '20px',
    md: '24px',
    lg: '32px'
  } : (options.padding || '24px'),
  ...options.customStyles
});

// Utility function to create container-level scrollable page
export const createContainerScrollablePage = (theme, options = {}) => ({
  height: '100vh',
  maxHeight: '100vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  ...options.customStyles
});

// Utility function to calculate proper height offset for scrollable areas
export const calculateScrollableHeight = (options = {}) => {
  // Account for only the actual layout elements, no extra clearance for bottom-touching
  const headerHeight = options.headerHeight || (options.hasHeader !== false ? 85 : 0); // Updated header height
  const pageContentPadding = options.pageContentPadding || 48; // PageContent top/bottom padding (24px each)
  const containerPadding = options.containerPadding || 0; // Remove container padding from height calc
  const gridSpacing = options.gridSpacing || 16; // Grid spacing (2 * 8px) - reduced for better fit
  const extraClearance = options.extraClearance || 0; // No extra clearance for bottom-touching
  
  const totalOffset = headerHeight + pageContentPadding + containerPadding + gridSpacing + extraClearance;
  
  return `calc(100vh - ${totalOffset}px)`;
};

// Alternative calculation for when you know the exact layout
export const calculateScrollableHeightExact = (layoutConfig = {}) => {
  const {
    headerHeight = 64,
    topPadding = 24,
    bottomPadding = 24,
    containerTopPadding = 24,
    containerBottomPadding = 24,
    gridSpacing = 24,
    safetyMargin = 40
  } = layoutConfig;
  
  const totalOffset = headerHeight + topPadding + bottomPadding + 
                     containerTopPadding + containerBottomPadding + 
                     gridSpacing + safetyMargin;
  
  return `calc(100vh - ${totalOffset}px)`;
};

// Utility function to create scrollable content area within a layout
export const createScrollableContentArea = (theme, options = {}) => {
  const height = options.height || calculateScrollableHeight(options);
  
  return {
    height: height,
    maxHeight: height,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: options.paddingRight || 1, // Space for scrollbar
    paddingBottom: options.paddingBottom || 3, // Reduced bottom padding for bottom-touching
    marginBottom: options.marginBottom || 0, // No margin for bottom-touching
    ...createScrollbarStyles(theme, theme.palette.primary.main, options.scrollbarType || 'thin'),
    ...options.customStyles
  };
};

// Utility function to create sidebar + scrollable main content layout
export const createSidebarScrollableLayout = (theme, options = {}) => {
  const height = options.height || calculateScrollableHeight(options);
  
  return {
    container: {
      height: '100%',
      display: 'flex',
      flexDirection: 'row',
      gap: options.spacing || 3,
    },
    sidebar: {
      width: options.sidebarWidth || '280px',
      flexShrink: 0,
      height: 'fit-content',
      position: 'sticky',
      top: options.stickyTop || 24,
    },
    mainContent: {
      flex: 1,
      height: height,
      maxHeight: height,
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingRight: 1, // Space for scrollbar
      paddingBottom: options.paddingBottom || 3, // Reduced bottom padding for bottom-touching
      marginBottom: options.marginBottom || 0, // No margin for bottom-touching
      ...createScrollbarStyles(theme, theme.palette.primary.main, 'thin'),
    },
    ...options.customStyles
  };
};

// Utility function to create glassmorphism effect
export const createGlassmorphism = (theme, opacity = 0.7) => ({
  backdropFilter: 'blur(16px) saturate(180%)',
  WebkitBackdropFilter: 'blur(16px) saturate(180%)',
  backgroundColor: theme.palette.mode === 'dark'
    ? `rgba(255, 255, 255, ${opacity * 0.05})`
    : `rgba(255, 255, 255, ${opacity})`,
  border: '1px solid',
  borderColor: theme.palette.mode === 'dark'
    ? `rgba(255, 255, 255, ${opacity * 0.1})`
    : `rgba(255, 255, 255, ${opacity * 0.3})`,
  boxShadow: `
    0 8px 32px ${theme.palette.mode === 'dark' 
      ? 'rgba(0, 0, 0, 0.3)' 
      : 'rgba(0, 0, 0, 0.08)'},
    inset 0 0 0 1px ${theme.palette.mode === 'dark'
      ? `rgba(255, 255, 255, ${opacity * 0.08})`
      : `rgba(255, 255, 255, ${opacity * 0.2})`}
  `,
});

// Utility function to create hover effects
export const createHoverEffect = (theme, color, intensity = 0.08) => ({
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(color, intensity),
    transform: 'translateY(-1px)',
    boxShadow: `0 4px 12px ${alpha(color, intensity * 2)}`,
  }
});

// Utility function to create focus effects
export const createFocusEffect = (theme, color) => ({
  '&:focus': {
    outline: 'none',
    boxShadow: `0 0 0 3px ${alpha(color, 0.2)}`,
  }
});

// Utility function to create loading shimmer effect
export const createShimmerEffect = (theme) => ({
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(
      90deg,
      transparent 0%,
      ${alpha(theme.palette.primary.main, 0.2)} 50%,
      transparent 100%
    )`,
    transform: 'translateX(-100%)',
    animation: 'shimmer 2s infinite',
  }
});

// Utility function to create construction pattern
export const createConstructionPattern = (theme, color) => ({
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 10px,
      ${alpha(color, 0.06)} 10px,
      ${alpha(color, 0.06)} 20px
    )`,
    backgroundSize: '28px 28px',
    animation: 'construction 8s linear infinite',
    pointerEvents: 'none',
    zIndex: 0,
    opacity: 0.5
  }
});

// Utility function to create dot pattern background
export const createDotPattern = (theme, color, size = 20) => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -50,
    left: -50,
    right: -50,
    bottom: -50,
    backgroundImage: `radial-gradient(${alpha(color, 0.03)} 1px, transparent 1px)`,
    backgroundSize: `${size}px ${size}px`,
    animation: 'moveBackground 20s linear infinite',
    opacity: 0.5,
    pointerEvents: 'none'
  }
});

// Utility function to create gradient background
export const createGradientBackground = (theme, primaryColor, secondaryColor) => ({
  background: `
    radial-gradient(circle at 90% 90%, ${alpha(primaryColor, 0.06)}, transparent 50%),
    radial-gradient(circle at 10% 10%, ${alpha(secondaryColor, 0.06)}, transparent 50%),
    ${theme.palette.background.paper}
  `,
});

// Enhanced scrollbar utility with different types
export const createScrollbarStyles = (theme, color, type = 'default') => {
  const baseStyles = {
    scrollBehavior: 'smooth',
    WebkitOverflowScrolling: 'touch',
  };

  switch (type) {
    case 'thin':
      return {
        ...baseStyles,
        '&::-webkit-scrollbar': {
          width: '6px',
          height: '6px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(color, 0.2),
          borderRadius: '3px',
          '&:hover': {
            background: alpha(color, 0.3),
          }
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${alpha(color, 0.2)} transparent`,
      };
    
    case 'main':
      return {
        ...baseStyles,
        '&::-webkit-scrollbar': {
          width: '12px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(color, 0.15),
          borderRadius: '6px',
          border: '3px solid transparent',
          backgroundClip: 'padding-box',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: alpha(color, 0.25),
          },
          '&:active': {
            background: alpha(color, 0.35),
          }
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          border: 'none',
          borderRadius: '6px',
        },
        '&::-webkit-scrollbar-corner': {
          background: 'transparent',
        },
        scrollbarWidth: 'auto',
        scrollbarColor: `${alpha(color, 0.15)} transparent`,
      };
    
    default:
      return {
        ...baseStyles,
        '&::-webkit-scrollbar': {
          width: '8px',
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(color, 0.2),
          borderRadius: '20px',
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
          transition: 'background 0.2s ease',
          '&:hover': {
            background: alpha(color, 0.3),
          }
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
          border: 'none',
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${alpha(color, 0.2)} transparent`,
      };
  }
};

// Utility function to create card styles
export const createCardStyles = (theme, elevation = 1) => {
  const isDark = theme.palette.mode === 'dark';
  const lightShadowMD = '0 4px 6px -1px rgba(5, 3, 21, 0.06), 0 2px 4px -1px rgba(5, 3, 21, 0.04)';
  const lightShadowLG = '0 10px 15px -3px rgba(5, 3, 21, 0.06), 0 4px 6px -2px rgba(5, 3, 21, 0.04)';
  const lightShadowSM = '0 1px 2px 0 rgba(5, 3, 21, 0.04)';
  
  return {
    border: '1px solid',
    borderColor: theme.palette.divider,
    borderRadius: 8,
    bgcolor: theme.palette.background.paper,
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    boxShadow: elevation > 0 ? (isDark ? SHADOWS.MD : lightShadowMD) : 'none',
    '&:hover': {
      boxShadow: elevation > 0 ? (isDark ? SHADOWS.LG : lightShadowLG) : (isDark ? SHADOWS.SM : lightShadowSM),
      transform: 'translateY(-1px)',
    }
  };
};

// Utility function to create button styles
export const createButtonStyles = (theme, variant = 'contained', color) => {
  const baseStyles = {
    textTransform: 'none',
    borderRadius: 8,
    fontWeight: 500,
    transition: `all ${TRANSITIONS.NORMAL} ${EASINGS.EASE_OUT}`,
  };

  switch (variant) {
    case 'contained':
      return {
        ...baseStyles,
        backgroundColor: color,
        color: '#ffffff',
        '&:hover': {
          backgroundColor: alpha(color, 0.8),
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
        }
      };
    case 'outlined':
      return {
        ...baseStyles,
        borderColor: alpha(color, 0.5),
        color: color,
        '&:hover': {
          backgroundColor: alpha(color, 0.08),
          borderColor: color,
        }
      };
    case 'text':
      return {
        ...baseStyles,
        color: color,
        '&:hover': {
          backgroundColor: alpha(color, 0.08),
        }
      };
    default:
      return baseStyles;
  }
};

// Utility function to create input styles
export const createInputStyles = (theme, color) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : '#f0f2f5',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.08)' 
        : '#ede8e4',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.12)' 
        : '#e8e2dd',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: alpha(color, 0.5)
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: color,
      borderWidth: '2px'
    }
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    '&.Mui-focused': {
      color: color
    }
  }
});

// Utility function to create table styles
export const createTableStyles = (theme, color) => ({
  '& .MuiTableHead-root': {
    '& .MuiTableCell-root': {
      backgroundColor: alpha(theme.palette.background.default, 0.5),
      fontWeight: 600,
      fontSize: '0.875rem',
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    }
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      '&:hover': {
        backgroundColor: alpha(color, 0.04)
      }
    }
  },
  '& .MuiTableCell-root': {
    borderColor: theme.palette.divider,
    fontSize: '0.875rem',
    padding: '12px 16px'
  }
});

// Utility function to create animation styles
export const createAnimationStyles = (animationName, duration = '0.3s', easing = EASINGS.EASE_OUT) => ({
  animation: `${animationName} ${duration} ${easing}`,
});

// Utility function to create responsive styles
export const createResponsiveStyles = (styles) => ({
  xs: styles.xs || {},
  sm: styles.sm || {},
  md: styles.md || {},
  lg: styles.lg || {},
  xl: styles.xl || {},
});

// Utility function to create theme-aware styles
export const createThemeAwareStyles = (lightStyles, darkStyles) => (theme) => 
  theme.palette.mode === 'dark' ? darkStyles : lightStyles;

// Utility function to get contrast color
export const getContrastColor = (theme, color) => {
  // Simple contrast calculation - in a real app you might want to use a more sophisticated method
  const rgb = color.match(/\d+/g);
  if (!rgb) return theme.palette.text.primary;
  
  const brightness = (parseInt(rgb[0]) * 299 + parseInt(rgb[1]) * 587 + parseInt(rgb[2]) * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
};

// Utility function to create status colors
export const createStatusColors = (theme) => ({
  success: theme.palette.success.main,
  warning: theme.palette.warning.main,
  error: theme.palette.error.main,
  info: theme.palette.info.main,
  neutral: theme.palette.grey[500],
});

// Utility function to create elevation styles
export const createElevationStyles = (level = 1) => {
  const shadows = {
    0: 'none',
    1: SHADOWS.SM,
    2: SHADOWS.MD,
    3: SHADOWS.LG,
    4: SHADOWS.XL,
  };
  
  return {
    boxShadow: shadows[level] || shadows[1],
  };
};

// Utility function to create sidebar card styles
export const createSidebarCardStyles = (theme, options = {}) => ({
  ...COMMON_STYLES.card,
  height: 'fit-content',
  position: 'sticky',
  top: options.stickyTop || 24,
  ...options.customStyles
});

// Utility function to create dialog styles
export const createDialogStyles = (theme, options = {}) => ({
  '& .MuiDialog-paper': {
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: options.borderRadius || 8,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `
      0 24px 48px ${alpha(theme.palette.common.black, 0.15)},
      0 8px 16px ${alpha(theme.palette.common.black, 0.1)}
    `,
    minWidth: options.minWidth || 400,
    maxWidth: options.maxWidth || 600,
    ...options.customStyles
  }
});

// Utility function to create snackbar styles (simple, clean, green/red by severity)
export const createSnackbarStyles = (theme, options = {}) => ({
  zIndex: 1400,
  '& .MuiSnackbarContent-root': {
    minWidth: 'auto'
  },
  '& .MuiPaper-root, & .MuiAlert-root': {
    position: 'relative',
    minWidth: options.minWidth || 280,
    maxWidth: options.maxWidth || 420,
    borderRadius: options.borderRadius ?? 14,
    overflow: 'hidden',
    animation: 'snackbarSlideUp 0.35s cubic-bezier(0.21, 0.53, 0.35, 1)',
    boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.15)}`,
    '&::before': { display: 'none' },
    '&::after': { display: 'none' },

    '& .MuiAlert-message': {
      fontFamily: 'StrawberrySansText',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: 'inherit'
    },
    '& .MuiAlert-icon': {
      fontSize: '1.25rem',
      opacity: 0.95,
      color: 'inherit'
    },
    '& .MuiAlert-action .MuiIconButton-root': {
      color: 'rgba(255,255,255,0.9)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.12)',
        color: '#fff'
      }
    },

    '&.MuiAlert-filledSuccess': {
      backgroundColor: theme.palette.success.main,
      color: '#fff',
      boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.35)}`,
      '& .MuiAlert-icon': { color: '#fff' }
    },
    '&.MuiAlert-filledError': {
      backgroundColor: theme.palette.error.main,
      color: '#fff',
      boxShadow: `0 4px 20px ${alpha(theme.palette.error.main, 0.35)}`,
      '& .MuiAlert-icon': { color: '#fff' }
    },
    '&.MuiAlert-filledWarning': {
      backgroundColor: theme.palette.warning.main,
      color: '#fff',
      boxShadow: `0 4px 20px ${alpha(theme.palette.warning.main, 0.35)}`,
      '& .MuiAlert-icon': { color: '#fff' }
    },
    '&.MuiAlert-filledInfo': {
      backgroundColor: theme.palette.info.main,
      color: '#fff',
      boxShadow: `0 4px 20px ${alpha(theme.palette.info.main, 0.35)}`,
      '& .MuiAlert-icon': { color: '#fff' }
    },

    ...options.customStyles
  },

  '@global': {
    '@keyframes snackbarSlideUp': {
      '0%': {
        opacity: 0,
        transform: 'translateY(20px)'
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0)'
      }
    }
  }
});

// Utility function to create popover styles
export const createPopoverStyles = (theme, options = {}) => ({
  '& .MuiPopover-paper': {
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(12px) saturate(180%)',
    WebkitBackdropFilter: 'blur(12px) saturate(180%)',
    borderRadius: options.borderRadius || 8,
    border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    boxShadow: `
      0 12px 40px ${alpha(theme.palette.common.black, 0.15)},
      0 4px 16px ${alpha(theme.palette.common.black, 0.1)}
    `,
    ...options.customStyles
  }
});

// Utility function to create tooltip styles
export const createTooltipStyles = (theme, options = {}) => ({
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha('#000000', 0.9) 
    : alpha('#2f3337', 0.9),
  color: '#ffffff',
  fontSize: options.fontSize || '0.75rem',
  borderRadius: options.borderRadius || 6,
  padding: options.padding || '8px 12px',
  boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  ...options.customStyles
});

// Utility function to create drawer styles
export const createDrawerStyles = (theme, options = {}) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: alpha(theme.palette.background.paper, 0.98),
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
    boxShadow: `
      0 8px 32px ${alpha(theme.palette.common.black, 0.12)},
      inset 0 0 0 1px ${alpha(theme.palette.common.white, 0.05)}
    `,
    width: options.width || 280,
    ...options.customStyles
  }
});

// Utility function to create menu styles
export const createMenuStyles = (theme, options = {}) => ({
  '& .MuiPaper-root': {
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: options.borderRadius || 8,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.15)}`,
    padding: '8px',
    ...options.customStyles
  },
  '& .MuiMenuItem-root': {
    borderRadius: 6,
    margin: '2px 0',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      transform: 'translateX(2px)'
    }
  }
});

// Utility function to create backdrop styles
export const createBackdropStyles = (theme, options = {}) => ({
  backgroundColor: alpha(theme.palette.common.black, options.opacity || 0.6),
  backdropFilter: `blur(${options.blur || 6}px)`,
  WebkitBackdropFilter: `blur(${options.blur || 6}px)`,
  transition: 'all 0.3s ease',
  ...options.customStyles
});

// Utility function to create main content card styles
export const createMainContentCardStyles = (theme, options = {}) => ({
  ...COMMON_STYLES.card,
  minHeight: options.minHeight || 'auto',
  ...options.customStyles
});

// Utility function to create card-based page layout
export const createCardBasedPageLayout = (theme, options = {}) => ({
  container: {
    spacing: options.spacing || 3,
    sx: { height: '100%' }
  },
  sidebarGrid: {
    xs: 12,
    md: options.sidebarWidth || 3
  },
  mainGrid: {
    xs: 12,
    md: 12 - (options.sidebarWidth || 3)
  }
});

// Utility function to create navigation card styles
export const createNavigationCardStyles = (theme, options = {}) => ({
  ...COMMON_STYLES.card,
  position: 'sticky',
  top: options.stickyTop || 24,
  '& .MuiList-root': {
    padding: 0
  },
  '& .MuiListItem-root': {
    borderRadius: 2,
    mb: 1,
    px: 2,
    py: 1.5,
    transition: 'all 0.2s ease',
    '&.Mui-selected': {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: 'primary.main',
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15),
      }
    },
    '&:hover': {
      backgroundColor: alpha(theme.palette.primary.main, 0.05),
    }
  },
  '& .MuiListItemIcon-root': {
    minWidth: 40,
    transition: 'color 0.2s ease'
  },
  '& .MuiListItemText-primary': {
    fontSize: '0.875rem',
    transition: 'all 0.2s ease'
  },
  ...options.customStyles
});

// Utility function to create split-layout dialog styles
export const createSplitDialogStyles = (theme, options = {}) => ({
  '& .MuiDialog-paper': {
    backgroundColor: theme.palette.background.paper,
    borderRadius: options.borderRadius || 2,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: `
      0 24px 48px ${alpha(theme.palette.common.black, 0.15)},
      0 8px 16px ${alpha(theme.palette.common.black, 0.1)}
    `,
    minWidth: options.minWidth || 600,
    maxWidth: options.maxWidth || 800,
    minHeight: options.minHeight || 400,
    maxHeight: options.maxHeight || '90vh',
    ...options.customStyles
  }
});

// Utility function to create dialog sidebar styles
export const createDialogSidebarStyles = (theme, options = {}) => ({
  bgcolor: options.backgroundColor || 'primary.main',
  color: options.color || 'white',
  p: options.padding || 3,
  width: { 
    xs: '100%', 
    md: options.width || '240px' 
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative',
  overflow: 'hidden',
  
  // Add subtle pattern or gradient if specified
  ...(options.pattern && {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: options.pattern,
      opacity: 0.1,
      pointerEvents: 'none'
    }
  }),
  
  ...options.customStyles
});

// Utility function to create dialog main content styles
export const createDialogMainContentStyles = (theme, options = {}) => ({
  flex: 1,
  p: options.padding || 3,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: options.backgroundColor || theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  ...options.customStyles
});

// Utility function to create feature card styles (for dialog content)
export const createFeatureCardStyles = (theme, options = {}) => ({
  flex: 1,
  p: options.padding || 2,
  border: '1px solid',
  borderColor: options.borderColor || 'divider',
  borderRadius: options.borderRadius || 2,
  bgcolor: options.backgroundColor || 'background.paper',
  transition: 'all 0.2s ease',
  cursor: options.clickable ? 'pointer' : 'default',
  elevation: options.elevation || 0,
  
  ...(options.clickable && {
    '&:hover': {
      borderColor: 'primary.main',
      backgroundColor: alpha(theme.palette.primary.main, 0.02),
      transform: 'translateY(-1px)',
      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`
    }
  }),
  
  ...options.customStyles
});

// Utility function to create upload zone styles
export const createUploadZoneStyles = (theme, options = {}) => ({
  mt: options.marginTop || 3,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: options.paddingY || 3,
  px: options.paddingX || 2,
  borderRadius: options.borderRadius || 2,
  bgcolor: options.backgroundColor || 'rgba(0, 0, 0, 0.02)',
  border: options.borderStyle || '2px dashed',
  borderColor: options.borderColor || 'divider',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  
  '&:hover': {
    borderColor: 'primary.main',
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
    transform: 'translateY(-1px)',
    
    '& .upload-icon': {
      transform: 'scale(1.1)',
      color: 'primary.main'
    }
  },
  
  '&:active': {
    transform: 'translateY(0)',
  },
  
  // Drag and drop states
  '&.drag-over': {
    borderColor: 'primary.main',
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderStyle: 'solid',
  },
  
  ...options.customStyles
});

// Utility function to create dialog header styles
export const createDialogHeaderStyles = (theme, options = {}) => ({
  padding: options.padding || '0 0 16px 0',
  borderBottom: options.showBorder ? `1px solid ${theme.palette.divider}` : 'none',
  marginBottom: options.marginBottom || 2,
  
  '& .MuiTypography-root': {
    fontWeight: options.fontWeight || 'bold',
    color: options.color || theme.palette.text.primary,
    fontSize: options.fontSize || '1.25rem'
  },
  
  ...options.customStyles
});

// Utility function to create dialog content area styles
export const createDialogContentAreaStyles = (theme, options = {}) => ({
  padding: options.padding || 0,
  overflow: options.scrollable ? 'auto' : 'visible',
  flex: options.flex ? 1 : 'none',
  
  ...(options.scrollable && {
    ...createScrollbarStyles(theme, theme.palette.primary.main, 'thin'),
    maxHeight: options.maxHeight || '60vh'
  }),
  
  ...options.customStyles
});

// Utility function to create dialog actions styles
export const createDialogActionsStyles = (theme, options = {}) => ({
  p: options.padding || '0',
  pt: options.paddingTop || 2,
  justifyContent: options.justifyContent || 'flex-end',
  gap: options.gap || 1,
  borderTop: options.showBorder ? `1px solid ${theme.palette.divider}` : 'none',
  mt: options.marginTop || 0,
  
  '& .MuiButton-root': {
    borderRadius: options.buttonBorderRadius || 2,
    textTransform: 'none',
    minWidth: options.buttonMinWidth || '100px',
    fontWeight: options.buttonFontWeight || 'medium'
  },
  
  ...options.customStyles
});

// Utility function to create info section styles (for dialog sidebars)
export const createInfoSectionStyles = (theme, options = {}) => ({
  opacity: options.opacity || 0.8,
  marginTop: options.marginTop || 4,
  
  '& .info-icon': {
    fontSize: options.iconSize || '80px',
    opacity: options.iconOpacity || 0.3,
    marginBottom: options.iconMarginBottom || 2,
    transition: 'all 0.3s ease'
  },
  
  '& .info-text': {
    fontWeight: options.textFontWeight || 'medium',
    marginBottom: options.textMarginBottom || 1
  },
  
  '& .info-detail': {
    fontSize: options.detailFontSize || '0.875rem',
    opacity: options.detailOpacity || 0.9
  },
  
  ...options.customStyles
});

// Utility function to create tip/hint styles
export const createTipStyles = (theme, options = {}) => ({
  marginTop: options.marginTop || 2,
  textAlign: options.textAlign || 'center',
  
  '& .tip-label': {
    fontWeight: options.labelFontWeight || 'medium',
    color: options.labelColor || theme.palette.info.main
  },
  
  '& .tip-text': {
    color: options.textColor || theme.palette.text.secondary,
    fontSize: options.fontSize || '0.875rem'
  },
  
  ...options.customStyles
});

// Comprehensive dialog layout utility
export const createDialogLayout = (theme, layoutType = 'split', options = {}) => {
  const layouts = {
    // Split layout (sidebar + main content)
    split: {
      container: {
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        height: { md: options.height || '490px' },
        minHeight: options.minHeight || '400px'
      },
      sidebar: createDialogSidebarStyles(theme, options.sidebar),
      mainContent: createDialogMainContentStyles(theme, options.mainContent)
    },
    
    // Standard single-column layout
    standard: {
      container: {
        padding: options.padding || 3,
        minWidth: options.minWidth || 400,
        maxWidth: options.maxWidth || 600
      }
    },
    
    // Full-width layout
    fullWidth: {
      container: {
        padding: options.padding || 3,
        width: '100%',
        maxWidth: options.maxWidth || '90vw',
        height: options.height || 'auto',
        maxHeight: options.maxHeight || '90vh'
      }
    },
    
    // Wizard/stepper layout
    wizard: {
      container: {
        padding: options.padding || 3,
        minWidth: options.minWidth || 600,
        maxWidth: options.maxWidth || 800,
        minHeight: options.minHeight || 500
      },
      header: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        paddingBottom: 2,
        marginBottom: 3
      },
      content: {
        flex: 1,
        overflow: 'auto',
        ...createScrollbarStyles(theme, theme.palette.primary.main, 'thin')
      },
      actions: {
        borderTop: `1px solid ${theme.palette.divider}`,
        paddingTop: 2,
        marginTop: 3,
        justifyContent: 'space-between'
      }
    }
  };
  
  return layouts[layoutType] || layouts.standard;
};

// Utility function to create update notification styles
export const createUpdateNotificationStyles = (theme, options = {}) => ({
  container: {
    position: 'relative',
    minWidth: 320,
    maxWidth: 380,
    background: theme.palette.background.paper,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius: 3,
    border: '1px solid',
    borderColor: theme.palette.divider,
    boxShadow: `
      0 20px 40px ${alpha(theme.palette.common.black, 0.15)},
      0 8px 16px ${alpha(theme.palette.common.black, 0.08)},
      inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.05 : 0.1)}
    `,
    overflow: 'hidden',
    animation: 'slideUpBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '1px',
      background: `linear-gradient(90deg, transparent 0%, ${alpha(theme.palette.primary.main, 0.3)} 50%, transparent 100%)`,
      zIndex: 1,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `radial-gradient(circle at 50% 0%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 70%)`,
      pointerEvents: 'none',
      zIndex: 0,
    },
    ...options.containerStyles
  },
  
  header: {
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'space-between',
    p: 2,
    pb: 1.5,
    ...options.headerStyles
  },
  
  iconContainer: {
    p: 1,
    borderRadius: 1.5,
    background: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...options.iconContainerStyles
  },
  
  title: {
    color: theme.palette.text.primary,
    fontWeight: 600,
    fontSize: '1.05rem',
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
    mb: 0.5,
    fontFamily: 'StrawberrySansDisplay',
    ...options.titleStyles
  },
  
  chip: {
    height: 22,
    fontSize: '0.7rem',
    fontWeight: 600,
    bgcolor: 'background.paper',
    color: 'text.secondary',
    border: 'none',
    borderRadius: '12px',
    '& .MuiChip-label': { px: 1 },
    ...options.chipStyles
  },
  
  closeButton: {
    minWidth: 'auto',
    p: 0.5,
    borderRadius: 1.5,
    color: theme.palette.text.secondary,
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main
    },
    ...options.closeButtonStyles
  },
  
  divider: {
    borderColor: theme.palette.divider,
    mx: 2,
    ...options.dividerStyles
  },
  
  actionsContainer: {
    p: 2, 
    pt: 1.5,
    ...options.actionsContainerStyles
  },
  
  secondaryButton: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    px: 1.5,
    py: 0.75,
    borderRadius: 2,
    textTransform: 'none',
    fontSize: '0.875rem',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.08),
      color: theme.palette.primary.main,
      transform: 'translateY(-1px)'
    },
    ...options.secondaryButtonStyles
  },
  
  primaryButton: {
    fontWeight: 600,
    px: 2.5,
    py: 0.75,
    borderRadius: 2,
    textTransform: 'none',
    fontSize: '0.875rem',
    bgcolor: theme.palette.primary.main,
    color: 'white',
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
    transition: 'all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
    '&:hover': {
      bgcolor: theme.palette.primary.dark,
      color: 'white',
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.35)}`
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
    },
    ...options.primaryButtonStyles
  },
  
  minimizedIndicator: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: theme.palette.background.paper,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid',
    borderColor: theme.palette.divider,
    boxShadow: `
      0 12px 32px ${alpha(theme.palette.common.black, 0.15)},
      0 4px 16px ${alpha(theme.palette.common.black, 0.08)}
    `,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    zIndex: 1400,
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    color: theme.palette.primary.main,
    '&:hover': {
      transform: 'scale(1.1)',
      boxShadow: `
        0 16px 40px ${alpha(theme.palette.common.black, 0.2)},
        0 6px 20px ${alpha(theme.palette.common.black, 0.1)}
      `,
      bgcolor: alpha(theme.palette.primary.main, 0.08)
    },
    '&:active': {
      transform: 'scale(0.95)',
      transition: 'transform 0.1s ease'
    },
    ...options.minimizedIndicatorStyles
  },
  
  animations: {
    '@keyframes slideUpBounce': {
      '0%': {
        opacity: 0,
        transform: 'translateY(50px) scale(0.8)'
      },
      '60%': {
        opacity: 1,
        transform: 'translateY(-10px) scale(1.05)'
      },
      '100%': {
        opacity: 1,
        transform: 'translateY(0) scale(1)'
      }
    },
    '@keyframes glowPulse': {
      '0%, 100%': {
        boxShadow: `
          0 20px 40px ${alpha(theme.palette.primary.main, 0.25)},
          0 8px 20px ${alpha(theme.palette.common.black, 0.15)},
          inset 0 1px 0 ${alpha(theme.palette.common.white, 0.2)}
        `
      },
      '50%': {
        boxShadow: `
          0 25px 50px ${alpha(theme.palette.primary.main, 0.35)},
          0 12px 30px ${alpha(theme.palette.common.black, 0.2)},
          inset 0 1px 0 ${alpha(theme.palette.common.white, 0.3)}
        `
      }
    },
    '@keyframes shimmerEffect': {
      '0%': {
        left: '-100%'
      },
      '100%': {
        left: '100%'
      }
    },
    '@keyframes floatBounce': {
      '0%, 100%': {
        transform: 'translateY(0px)'
      },
      '50%': {
        transform: 'translateY(-3px)'
      }
    },
    '@keyframes rotateBorder': {
      '0%': {
        transform: 'rotate(0deg)'
      },
      '100%': {
        transform: 'rotate(360deg)'
      }
    }
  }
});

// Export all utility functions
export {
  COMMON_STYLES,
  ANIMATIONS,
  SHADOWS,
  TRANSITIONS,
  EASINGS,
  PAGE_LAYOUT
}; 